<template>
  <div class="mb-1">
    <v-row no-gutters align="start">
      <v-col cols="5">
        <v-text-field
          solo
          dense
          :clearable="!internalValue || internalValue === ''"
          :value="internalKey"
          @input="onKeyInput"
          placeholder="Namespace"
          :rules="keyRules"
          hide-details="auto"
        >
          <!-- Undo for key -->
          <template
            v-slot:append
            v-if="originalKey !== undefined && originalKey !== internalKey"
          >
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small @click="undoKey">
                  mdi-undo
                </v-icon>
              </template>
              <span>{{ originalKey || 'blank' }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>

      <v-col
        cols="1"
        class="text-center d-flex align-center justify-center"
        style="height: 40px"
      >
        <v-icon small color="secondary">mdi-arrow-right</v-icon>
      </v-col>

      <v-col cols="5">
        <v-text-field
          solo
          dense
          clearable
          :value="internalValue"
          @input="updateValue"
          placeholder="Value"
          :rules="valueRules"
          :error-messages="valueErrorMessages"
          :disabled="!internalKey || internalKey.trim() === ''"
          hide-details="auto"
        >
          <!-- Undo for value -->
          <template
            v-slot:append
            v-if="
              originalValue !== undefined && originalValue !== internalValue
            "
          >
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small @click="undoValue">
                  mdi-undo
                </v-icon>
              </template>
              <span>{{ originalValue || 'blank' }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="1" class="text-center">
        <!-- Delete button when both key and value exist -->
        <v-btn v-if="internalKey && internalValue" icon @click="deletePair">
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  props: {
    keyValue: {
      type: String,
      default: '',
    },
    valueValue: {
      type: String,
      default: '',
    },
    existingKeys: {
      type: Array,
      default: () => [],
    },
    originalKey: {
      type: String,
      default: undefined,
    },
    originalValue: {
      type: String,
      default: undefined,
    },
    isLastEntry: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalKey: this.keyValue || '',
      internalValue: this.valueValue || '',
    }
  },
  computed: {
    keyRules() {
      return [this.rules.required, this.rules.validKey, this.rules.uniqueKey]
    },
    valueRules() {
      // Require value if key is present and not empty
      if (this.internalKey && this.internalKey.trim() !== '') {
        return [this.rules.required]
      }
      return []
    },
    valueErrorMessages() {
      // Show immediate error messages when key exists but value is missing
      if (
        this.internalKey &&
        this.internalKey.trim() !== '' &&
        !this.internalValue
      ) {
        return ['Required']
      }
      return []
    },
    rules() {
      return {
        required: (value) => !!value || 'Required',
        validKey: (value) => {
          if (!value) return 'Required'
          return (
            /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(value) ||
            'Must start with letter, use only letters, numbers, _ or -'
          )
        },
        uniqueKey: (value) => {
          if (!value) return true // Let required rule handle empty
          const duplicates = this.existingKeys.filter((k) => k === value)
          return duplicates.length <= 1 || 'Duplicate key'
        },
      }
    },
  },
  watch: {
    keyValue(newVal) {
      if (newVal !== this.internalKey) {
        this.internalKey = newVal
      }
    },
    valueValue(newVal) {
      if (newVal !== this.internalValue) {
        this.internalValue = newVal
      }
    },
  },
  methods: {
    onKeyInput(value) {
      this.internalKey = value
      this.$emit('update:key', value)

      // If this is the last entry and now has both key and value, request new entry
      if (this.isLastEntry && value && this.internalValue) {
        this.$emit('request-new-entry')
      }
    },
    updateValue(newValue) {
      this.internalValue = newValue
      this.$emit('update:value', newValue)

      // If this is the last entry and now has both key and value, request new entry
      if (this.isLastEntry && this.internalKey && newValue) {
        this.$emit('request-new-entry')
      }
    },
    deletePair() {
      this.$emit('delete', {
        key: this.internalKey,
        value: this.internalValue,
      })
    },
    undoKey() {
      if (this.originalKey !== undefined) {
        this.internalKey = this.originalKey
        this.$emit('update:key', this.originalKey)
      }
    },
    undoValue() {
      if (this.originalValue !== undefined) {
        this.internalValue = this.originalValue
        this.$emit('update:value', this.originalValue)
      }
    },
  },
}
</script>

<style scoped>
/* Remove any custom spacing - let Vuetify handle it */
.mb-1 {
  margin-bottom: 4px !important;
}

/* Center the arrow and delete button columns */
.text-center {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
